import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getChildrenItems } from './getChildrenItems';
import { useQwanyx, persistQwantum } from 'qwanyx';
import { isAncestor } from './isAncestor';

export const StackItem = ({
  icon,
  text: initialText,
  isFolder = false,
  level = 0,
  selected = false,
  selectedItems = [],
  onSelect,
  itemId,
  isRoot = false,
  registerItem = null,
  itemData = null,
  onRename = null,
  onDrop = null,
  dragCompletedSignal = 0
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [children, setChildren] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [localItemData, setLocalItemData] = useState(itemData);
  const [isEditing, setIsEditing] = useState(false);
  const [displayText, setDisplayText] = useState(initialText);
  const [editText, setEditText] = useState(initialText);
  const [isHovering, setIsHovering] = useState(false);
  const inputRef = useRef(null);
  const iconRef = useRef(null); // Add a ref for the icon element

  // Get Qwanyx context for card update functionality
  const { updateCardByName, showCard } = useQwanyx();

  // Memoize fetchChildren to safely use in useEffect dependencies
  const fetchChildren = useCallback(async () => {
    if (!itemId) return;

    setIsLoading(true);
    try {
      const childItems = await getChildrenItems(itemId);

      childItems.forEach(item => {
        if (item._id === itemId && !localItemData) {
          setLocalItemData(item);
        }
      });

      const childrenItems = childItems.map(child => ({
        id: child._id,
        icon: child.isFolder ? 'fa-folder' : 'fa-file',
        text: child.title || 'Untitled',
        isFolder: child.isFolder,
        itemData: child
      }));

      setChildren(childrenItems);
    } catch (error) {
      console.error("Error fetching children:", error);
    } finally {
      setIsLoading(false);
    }
  }, [itemId, localItemData]);

  // Rest of your component's logic remains unchanged
  useEffect(() => {
    setDisplayText(initialText);
  }, [initialText]);

  useEffect(() => {
    if (registerItem && itemId && !isRoot) {
      registerItem(itemId, level, localItemData);
    }
  }, [registerItem, itemId, level, isRoot, localItemData]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    if (isRoot) {
      fetchChildren();
      return;
    }

    if (isFolder && isOpen && children.length === 0) {
      fetchChildren();
    }
  }, [isRoot, isFolder, isOpen, children.length, fetchChildren]);

  // New useEffect to respond to dragCompletedSignal
  useEffect(() => {
    // Skip if this is a new mount or dragCompletedSignal is 0
    if (dragCompletedSignal === 0) return;
    
    // Only perform refresh if not root and we have a valid itemId
    if (!isRoot && itemId) {
      // If this component is a folder, refresh it to check if items were added or removed
      if (isFolder) {
        fetchChildren();
      }
    }
  }, [dragCompletedSignal, isRoot, itemId, isFolder, fetchChildren]);

  const toggleFolder = (e) => {
    if (isFolder || isRoot) {
      e.stopPropagation();
      setIsOpen(!isOpen);
    }
  };

  const updateCurrentCard = () => {
    if (!itemId) return;

    const cardData = localItemData || {
      _id: itemId,
      ed: itemId,
      pa: null,
      di: null
    };

    const updateData = {
      _id: cardData._id || itemId,
      di: cardData.di || null,
      ed: cardData.ed || itemId,
      pa: cardData.pa || null,
      title: displayText,
      brief: cardData.brief,
      isFolder: cardData.isFolder,
      _forceUpdate: Date.now()
    };

    updateCardByName("currentCard", updateData);
    showCard("currentCard");
  };

  const handleClick = (e) => {
    // Don't handle clicks during drag operations
    if (document.querySelector('.dragging')) return;

    if (!isRoot && onSelect) {
      onSelect(itemId, e.shiftKey, e.ctrlKey || e.metaKey);

      if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
        updateCurrentCard();
      }
    }
  };

  // Updated handleDragStart with proper payload
  const handleDragStart = (e) => {
    e.stopPropagation();

    if (isRoot) {
      e.preventDefault();
      return; // Don't allow dragging the root
    }

    // Create payload with item data - ensure parent ID is included
    const payload = {
      items: [{
        itemId,
        isFolder,
        text: displayText,
        index: itemId,
        ed: localItemData?.ed || itemId,
        di: localItemData?.di || null,
        pa: localItemData?.pa || null,  // Make sure this is included from localItemData
        data: localItemData
      }]
    };

    // Set drag data as JSON string
    e.dataTransfer.setData('text/plain', JSON.stringify(payload));

    // Add dragging class for visual feedback
    e.currentTarget.classList.add('dragging');

    // Set allowed effect
    e.dataTransfer.effectAllowed = 'move';

    // Optional: Create a drag image
    const dragImage = document.createElement('div');
    dragImage.className = 'drag-ghost';
    dragImage.innerHTML = `
      <i class="fas ${isFolder ? 'fa-folder' : icon} small"></i>
      <span>${displayText}</span>
    `;
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 15, 15);

    // Clean up the temporary element after a short delay
    setTimeout(() => {
      document.body.removeChild(dragImage);
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.stopPropagation();
    e.currentTarget.classList.remove('dragging');
  };

  // Handle drag hover effects
  // Track whether we're hovering over the icon specifically
  const [isHoveringOverIcon, setIsHoveringOverIcon] = useState(false);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isRoot) {
      setIsHovering(true);
      // Check if we're hovering over the icon
      if (iconRef.current && iconRef.current.contains(e.target)) {
        setIsHoveringOverIcon(true);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (!isRoot) {
      setIsHovering(true);
      
      // Check if we're hovering over the icon
      const hoveredElement = document.elementFromPoint(e.clientX, e.clientY);
      if (iconRef.current && (iconRef.current === hoveredElement || iconRef.current.contains(hoveredElement))) {
        setIsHoveringOverIcon(true);
      } else {
        setIsHoveringOverIcon(false);
      }
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if we're leaving the component entirely or just the icon
    const relatedTarget = e.relatedTarget;
    if (!e.currentTarget.contains(relatedTarget)) {
      setIsHovering(false);
      setIsHoveringOverIcon(false);
    } else if (iconRef.current && !iconRef.current.contains(relatedTarget)) {
      setIsHoveringOverIcon(false);
    }
  };

  // Check if drop happened on the icon
  const isDropOnIcon = (e) => {
    if (!iconRef.current) return false;
    
    const iconRect = iconRef.current.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    return (
      x >= iconRect.left &&
      x <= iconRect.right &&
      y >= iconRect.top &&
      y <= iconRect.bottom
    );
  };

  // Updated handleDrop to include circular reference check and icon drop detection
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsHovering(false);
    setIsHoveringOverIcon(false); // Reset the icon hover state

    try {
      // Parse the data from the drag event
      const payload = JSON.parse(e.dataTransfer.getData('text/plain'));
      const draggedItem = payload.items[0];

      // Check if drop is on the icon
      const droppedOnIcon = isDropOnIcon(e);
      
      // If dropped on icon, show alert
      if (droppedOnIcon) {
        //alert(`Dropped on icon! Item: ${draggedItem.text}, Target: ${displayText}`);
        // You can add additional custom handling here for icon drops
        // You might want to add different behavior for icon drops
        persistQwantum(itemId, "pa", draggedItem.data._id);
        return;
      }

      // Get the source parent ID (important for refreshing the source folder)
      const sourceParentId = draggedItem.pa;
      
      // Check for circular reference
      const willCreateCircularReference = await isAncestor(draggedItem.itemId, itemId);
      
      if (willCreateCircularReference) {
        console.error("Cannot move item: This would create a circular reference");
        alert("Cannot move item: This would create a circular reference");
        return;
      }
      
      // If the dragged item is dropped on itself, prevent the drop
      if (draggedItem.itemId === itemId) {
        console.error("Cannot move item: Item cannot be dropped on itself");
        alert("Cannot move item: Item cannot be dropped on itself");
        return;
      }
      
      // All your existing validation code...
      
      // If all validations pass, perform the updates
      try {
        // 1. Make the target item a folder if needed
        if (!isFolder) {
          const updatedItemData = {
            ...localItemData,
            isFolder: true
          };
          setLocalItemData(updatedItemData);
          await persistQwantum(itemId, "isFolder", true, false);
        }
        
        // 2. Update the dragged item
        await persistQwantum(draggedItem.itemId, "ed", itemId, false);
        
        // 3. Open the folder to show the newly added child
        setIsOpen(true);
        
        // 4. Refresh this folder's children
        await fetchChildren();
        
        // 5. Notify parent components about the change
        if (onDrop) {
          onDrop(draggedItem.itemId, itemId, sourceParentId);  // Pass the sourceParentId here
        }
        
        // Optional success notification
        console.log(`Successfully moved "${draggedItem.text}" to "${displayText}"`);
        
      } catch (error) {
        console.error("Failed to update items:", error);
        alert(`Error moving item: ${error.message}`);
      }
      
    } catch (error) {
      console.error("Error handling drop:", error);
    }
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    if (!isRoot) {
      setIsEditing(true);
      setEditText(displayText);
    }
  };

  const handleInputChange = (e) => {
    setEditText(e.target.value);
  };

  const handleInputBlur = () => {
    finishEditing();
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      finishEditing();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const finishEditing = async () => {
    setIsEditing(false);

    if (editText !== displayText && editText.trim() !== '') {
      setDisplayText(editText);

      if (localItemData) {
        const updatedItemData = {
          ...localItemData,
          title: editText
        };
        setLocalItemData(updatedItemData);
      }

      try {
        await persistQwantum(itemId, "title", editText, false);
        console.log("Title updated in database:", editText);

        updateCurrentCard();
      } catch (error) {
        console.error("Failed to update title in database:", error);
      }

      if (onRename) {
        onRename(itemId, editText);
      }
    } else {
      setEditText(displayText);
    }
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditText(displayText);
  };

  const isSelected = selectedItems.includes(itemId);

  if (isRoot) {
    return (
      <div
        style={{
          width: '100%',
          overflow: 'auto',
        }}
      >
        {isLoading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            Loading...
          </div>
        ) : children.length > 0 ? (
          children.map((child) => (
            <StackItem
              key={child.id}
              itemId={child.id}
              icon={child.icon}
              text={child.text}
              isFolder={child.isFolder}
              selectedItems={selectedItems}
              onSelect={onSelect}
              registerItem={registerItem}
              itemData={child.itemData}
              onRename={onRename}
              onDrop={onDrop}
              dragCompletedSignal={dragCompletedSignal}  // Pass the prop down
            />
          ))
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
            No items available
          </div>
        )}
      </div>
    );
  }

  const folderIcon = isFolder
    ? isOpen
      ? 'fa-folder-open'
      : 'fa-folder'
    : icon;

  // Determine background color based on selection and hover states
  const getBackgroundColor = () => {
    if (isSelected) {
      return isHovering ? '#c6e6ff' : '#e6f7ff';
    }
    return isHovering ? '#f0f7ff' : 'transparent';
  };

  // Determine border styles based on hover state
  const getBorderStyle = () => {
    if (isHovering && isFolder) {
      return '2px dashed #1890ff';
    } else if (isHovering) {
      return '2px dashed #40a9ff';
    }
    return '1px solid #eee';
  };

  return (
    <>
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px',
          paddingLeft: `${10 + level * 20}px`,
          borderBottom: getBorderStyle(),
          cursor: 'pointer',
          backgroundColor: getBackgroundColor(),
          transition: 'all 0.2s ease',
          position: 'relative'
        }}
        onClick={handleClick}
        draggable={!isRoot}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className="stack-item"
      >
        {isHovering && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '4px',
              backgroundColor: isFolder ? '#1890ff' : '#40a9ff'
            }}
          />
        )}
        <span style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {isFolder ? (
            <i
              ref={iconRef} // Add the ref to the icon
              className={`fas ${folderIcon} small`}
              onClick={toggleFolder}
              style={{ 
                cursor: 'pointer',
                color: isHoveringOverIcon ? '#2ecc71' : 'inherit', // Change to green when hovering
                transition: 'color 0.2s ease'
              }}
            />
          ) : (
            <i 
              ref={iconRef} // Add the ref to the icon
              className={`fas ${icon} small`} 
              style={{ 
                color: isHoveringOverIcon ? '#2ecc71' : 'inherit', // Change to green when hovering
                transition: 'color 0.2s ease'
              }}
            />
          )}

          {isEditing ? (
            <div style={{ marginLeft: '8px', width: '100%' }}>
              <input
                ref={inputRef}
                type="text"
                value={editText}
                className="input is-small"
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                style={{ width: '100%' }}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
              />
            </div>
          ) : (
            <span
              style={{ marginLeft: '8px', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
              onDoubleClick={handleDoubleClick}
            >
              {displayText}
            </span>
          )}
        </span>
        {isFolder && isLoading && (
          <span style={{ fontSize: '12px', color: '#999' }}>Loading...</span>
        )}
      </div>

      {isOpen && isFolder && children.length > 0 && children.map((child) => (
        <StackItem
          key={child.id}
          icon={child.icon}
          text={child.text}
          isFolder={child.isFolder}
          level={level + 1}
          itemId={child.id}
          selectedItems={selectedItems}
          onSelect={onSelect}
          registerItem={registerItem}
          itemData={child.itemData}
          onRename={onRename}
          onDrop={onDrop}
          dragCompletedSignal={dragCompletedSignal}  // Pass the prop down
        />
      ))}

      {isOpen && isFolder && children.length === 0 && !isLoading && (
        <div style={{
          padding: '10px',
          paddingLeft: `${10 + (level + 1) * 20}px`,
          color: '#999',
          fontStyle: 'italic'
        }}>
          No items
        </div>
      )}
    </>
  );
};

export default StackItem;